import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InlineDeleteButton } from "@/components/check-shared";
import { YawGauge } from "@/components/yaw-gauge";
import { YawFoilDiagram } from "@/components/yaw-foil-diagram";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconLock,
  IconLockOpen,
  IconPencil,
} from "@tabler/icons-react";
import { timeAgo } from "@/lib/time";
import {
  computeSide,
  YAW_LABELS,
  YAW_LABEL_DISPLAY,
  yawLabelBadgeClass,
  type SideMeasurements,
} from "@/domain/yaw-check";
import { asYawCheckId, type YawCheckId } from "@/types/identifiers";
import type { YawCheckActions } from "@/hooks/use-yaw-check-actions";
import type { Action } from "@/providers/auth-provider";
import type { components } from "@/api/generated/schema";

type YawCheck = components["schemas"]["YawCheckResponse"];
type YawCheckUpdate = components["schemas"]["YawCheckUpdate"];

interface YawCheckCardProps {
  check: YawCheck;
  actions: YawCheckActions;
  can: (action: Action) => boolean;
  onEditClick?: () => void;
  readOnly?: boolean;
}

function sideIncomplete(m: SideMeasurements): boolean {
  const c = computeSide(m);
  return c.tot_static === 0 || c.tot_max === 0 || c.tot_min === 0;
}

function extractSide(check: YawCheck, side: "prt" | "stb"): SideMeasurements {
  return {
    fwd_static: check[`${side}_fwd_static`],
    fwd_max: check[`${side}_fwd_max`],
    fwd_min: check[`${side}_fwd_min`],
    aft_static: check[`${side}_aft_static`],
    aft_max: check[`${side}_aft_max`],
    aft_min: check[`${side}_aft_min`],
  };
}

function MeasurementGrid({ check, side, label }: { check: YawCheck; side: "prt" | "stb"; label: string }) {
  const m = extractSide(check, side);
  const c = computeSide(m);

  return (
    <div className="mt-3">
      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">{label}</div>
      <div className="grid grid-cols-4 gap-x-3 text-xs tabular-nums">
        <div className="text-muted-foreground" />
        <div className="text-muted-foreground text-center">fwd</div>
        <div className="text-muted-foreground text-center">aft</div>
        <div className="text-muted-foreground text-center">tot</div>

        <div className="text-muted-foreground">static</div>
        <div className="text-center">{m.fwd_static.toFixed(2)}</div>
        <div className="text-center">{m.aft_static.toFixed(2)}</div>
        <div className="text-center font-medium">{c.tot_static.toFixed(2)}</div>

        <div className="text-muted-foreground">max</div>
        <div className="text-center">{m.fwd_max.toFixed(2)}</div>
        <div className="text-center">{m.aft_max.toFixed(2)}</div>
        <div className="text-center font-medium">{c.tot_max.toFixed(2)}</div>

        <div className="text-muted-foreground">min</div>
        <div className="text-center">{m.fwd_min.toFixed(2)}</div>
        <div className="text-center">{m.aft_min.toFixed(2)}</div>
        <div className="text-center font-medium">{c.tot_min.toFixed(2)}</div>

      </div>
      <div className="flex items-center gap-3 mt-1.5 text-xs tabular-nums">
        <span className="text-muted-foreground">YAW°</span>
        <span className="text-muted-foreground">min <span className="font-medium text-foreground">{c.tot_min_yaw.toFixed(2)}</span></span>
        <span className="text-muted-foreground">static <span className="font-medium text-foreground">{c.tot_yaw.toFixed(2)}</span></span>
        <span className="text-muted-foreground">max <span className="font-medium text-foreground">{c.tot_max_yaw.toFixed(2)}</span></span>
        <span className="text-muted-foreground ml-auto">range {c.range.toFixed(2)}°</span>
      </div>
    </div>
  );
}

export function YawCheckCard({ check, actions, can, onEditClick, readOnly = false }: YawCheckCardProps) {
  const [expanded, setExpanded] = useState(false);
  const checkId = asYawCheckId(check.id) as YawCheckId;
  const isSigned = !!check.signed_off_at;
  const canEdit = !readOnly;

  const prt = computeSide(extractSide(check, "prt"));
  const stb = computeSide(extractSide(check, "stb"));

  const prtIncomplete = sideIncomplete(extractSide(check, "prt"));
  const stbIncomplete = sideIncomplete(extractSide(check, "stb"));
  const hasMissing = prtIncomplete || stbIncomplete;

  const borderColor = hasMissing ? "border-l-amber-400" : isSigned ? "border-l-green-500" : "border-l-blue-400";

  return (
    <div className={`border-l-3 rounded-r-lg bg-card ${borderColor} relative`}>
      {/* Chevron — top right */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="absolute top-3 right-4 text-muted-foreground hover:text-foreground transition-colors"
      >
        <IconChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      <div className="pl-5 pr-9 pt-4 pb-3 flex flex-col sm:flex-row gap-3 sm:gap-4">
        {/* Left column: header, metadata, actions */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium">YAW</span>
            {hasMissing && (
              <span className="flex items-center gap-1 text-amber-500 dark:text-amber-400">
                <IconAlertTriangle className="h-3.5 w-3.5" />
                <span className="text-[11px]">{prtIncomplete && stbIncomplete ? "PORT + STBD incomplete" : prtIncomplete ? "PORT incomplete" : "STBD incomplete"}</span>
              </span>
            )}
          </div>

          {/* Metadata */}
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span>
              Checked by <span className="font-medium text-foreground">{check.checked_by}</span>
              <span className="text-muted-foreground/50 ml-1.5">{timeAgo(check.checked_at)}</span>
            </span>
            {canEdit && !isSigned ? (
              <Select
                value={check.label ?? "none"}
                onValueChange={(v) => actions.handleUpdate(checkId, { label: (v === "none" ? null : v) as YawCheckUpdate["label"] })}
              >
                <SelectTrigger className="h-6 w-auto min-w-[90px] text-[10px] px-2 py-0 border-dashed">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No label</SelectItem>
                  {YAW_LABELS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : check.label ? (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${yawLabelBadgeClass(check.label) ?? "bg-muted"}`}>
                {YAW_LABEL_DISPLAY[check.label] ?? check.label}
              </span>
            ) : null}
          </div>

          {/* Sign-off status */}
          {isSigned && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                <IconLock className="h-3 w-3" />
                Signed off by {check.signed_off_by}
              </span>
              {canEdit && can("manage-calibrations") && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => actions.handleUnlock(checkId)}
                      disabled={actions.isUnlocking}
                    >
                      <IconLockOpen className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Unlock</TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {!isSigned && canEdit && (
            <div className="mt-2 flex items-center gap-2">
              {can("sign-off") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-3 text-xs"
                  onClick={() => actions.handleSignOff(checkId)}
                  disabled={actions.isSigningOff}
                >
                  <IconLock className="mr-1 h-3 w-3" />
                  Sign off
                </Button>
              )}
              {can("manage-calibrations") && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 rounded-full text-muted-foreground/40 hover:text-foreground"
                        onClick={onEditClick}
                      >
                        <IconPencil className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Edit</TooltipContent>
                  </Tooltip>
                  <InlineDeleteButton
                    onConfirm={() => actions.handleDelete(checkId)}
                    disabled={actions.isDeleting}
                  />
                </>
              )}
            </div>
          )}
        </div>

        {/* Right column: foil diagram, stretches to fill card height */}
        <YawFoilDiagram
          prt={{ staticYaw: prt.tot_yaw, minYaw: prt.tot_min_yaw, maxYaw: prt.tot_max_yaw }}
          stb={{ staticYaw: stb.tot_yaw, minYaw: stb.tot_min_yaw, maxYaw: stb.tot_max_yaw }}
        />
      </div>

      {/* Gauge rows — full width below for breathing room on mobile */}
      <div className="px-5 pb-4 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground w-7 shrink-0">PRT</span>
          <YawGauge staticYaw={prt.tot_yaw} minYaw={prt.tot_min_yaw} maxYaw={prt.tot_max_yaw} />
          <span className="text-xs tabular-nums shrink-0 w-12 text-right">{prt.tot_yaw.toFixed(2)}°</span>
          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">±{prt.range.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground w-7 shrink-0">STB</span>
          <YawGauge staticYaw={stb.tot_yaw} minYaw={stb.tot_min_yaw} maxYaw={stb.tot_max_yaw} />
          <span className="text-xs tabular-nums shrink-0 w-12 text-right">{stb.tot_yaw.toFixed(2)}°</span>
          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">±{stb.range.toFixed(2)}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-4 border-t border-border/50">
          <MeasurementGrid check={check} side="prt" label="PRT (Port)" />
          <MeasurementGrid check={check} side="stb" label="STB (Starboard)" />
        </div>
      )}
    </div>
  );
}
